import includes from 'lodash/includes';
import React, { useCallback, useContext, useMemo } from 'react';

import connect from 'components/lib/connect';
import { getLatestConversationId } from 'actions/conversation/lib/conversation_helpers';
import { getLatestManualItem } from 'scripts/application/lib/conversation_history_helpers';
import ConversationItemType from 'models/conversation_item_type';
import { getTopGreetingSuggestion } from 'models/greeting_suggestion';
import UpdateGreetingSuggestionScore from 'actions/greeting_suggestions/update_greeting_suggestion_score';

export const DEFAULT_GREETING_SUGGESTIONS_CONTEXT = {
  getGreetingSuggestion: () => null,
};

const GreetingSuggestionsContext = React.createContext(DEFAULT_GREETING_SUGGESTIONS_CONTEXT);

export function GreetingSuggestionsContextProviderBase({
  children,
  agentHasSentMessage,
  greetingSuggestions,
  onUpdateGreetingSuggestionScore,
}) {
  const getGreetingSuggestion = useGetGreetingSuggestion(greetingSuggestions, agentHasSentMessage);

  const value = useMemo(
    () => ({
      getGreetingSuggestion,
      onUpdateGreetingSuggestionScore,
    }),
    [getGreetingSuggestion, onUpdateGreetingSuggestionScore]
  );

  return <GreetingSuggestionsContext.Provider value={value}>{children}</GreetingSuggestionsContext.Provider>;
}

const GreetingSuggestionsContextProvider = connect(
  mapStateToProps,
  mapExecuteToProps
)(GreetingSuggestionsContextProviderBase);

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const greetingSuggestionsProvider = getProvider('greetingSuggestions');
  const currentAgentId = getProvider('currentAgent').get().id;
  const latestConversationId = getLatestConversationId(getProvider('conversations'));
  const conversationHistory = getProvider('conversationHistory');

  return {
    greetingSuggestions: greetingSuggestionsProvider.findAll(),
    agentHasSentMessage: hasAgentSentMessage(conversationHistory, latestConversationId, currentAgentId),
  };
}

let onUpdateGreetingSuggestionScore;
function mapExecuteToProps(executeAction) {
  if (!onUpdateGreetingSuggestionScore) {
    onUpdateGreetingSuggestionScore = greetingSuggestionId => {
      executeAction(UpdateGreetingSuggestionScore, {
        greetingSuggestionId,
      });
    };
  }
  return { onUpdateGreetingSuggestionScore };
}

export default function GreetingSuggestionsProvider({ children }) {
  return <GreetingSuggestionsContextProvider>{children}</GreetingSuggestionsContextProvider>;
}

function useGetGreetingSuggestion(greetingSuggestions, agentHasSentMessage) {
  return useCallback(() => {
    if (agentHasSentMessage) {
      return null;
    }

    return getTopGreetingSuggestion(greetingSuggestions);
  }, [agentHasSentMessage, greetingSuggestions]);
}

export function hasAgentSentMessage(conversationHistory, latestConversationId, currentAgentId) {
  const messagingChannels = [
    ConversationItemType.SMS,
    ConversationItemType.FB_MESSAGE_OUTGOING,
    ConversationItemType.CHAT_MESSAGE,
    ConversationItemType.CONVERSATION_MESSAGE,
  ];
  return !!getLatestManualItem({
    conversationHistory,
    conversationId: latestConversationId,
    filter: ci => ci.initiator.id === currentAgentId && includes(messagingChannels, ci.content.type),
  });
}

export function useGreetingSuggestions() {
  return useContext(GreetingSuggestionsContext);
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/composer/contexts/greeting_suggestions_context.jsx